import { toast } from 'sonner';

/**
 * Notification utilities using Sonner
 * Provides themed toast notifications for the conference app
 */

export const notify = {
  /**
   * Success notification
   */
  success: (message: string, description?: string) => {
    toast.success(message, {
      description,
      duration: 3000,
    });
  },

  /**
   * Error notification
   */
  error: (message: string, description?: string) => {
    toast.error(message, {
      description,
      duration: 4000,
    });
  },

  /**
   * Info notification
   */
  info: (message: string, description?: string) => {
    toast.info(message, {
      description,
      duration: 3000,
    });
  },

  /**
   * Loading notification
   */
  loading: (message: string) => {
    return toast.loading(message);
  },

  /**
   * Dismiss a specific toast
   */
  dismiss: (toastId: string | number) => {
    toast.dismiss(toastId);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll: () => {
    toast.dismiss();
  },

  /**
   * Custom notification for specific app events
   */
  custom: {
    sessionSaved: () => {
      toast.success('Session saved to library', {
        description: 'Access it anytime from My Library',
      });
    },
    
    paperSaved: () => {
      toast.success('Paper saved', {
        description: 'Added to your library',
      });
    },
    
    speakerFollowed: (name: string) => {
      toast.success('Following speaker', {
        description: `You'll get updates about ${name}`,
      });
    },
    
    noteSaved: () => {
      toast.success('Note saved', {
        description: 'Auto-saved successfully',
      });
    },
    
    reportGenerated: () => {
      toast.success('Report generated', {
        description: 'Your AI report is ready',
        duration: 4000,
      });
    },
    
    meetingBooked: (company: string) => {
      toast.success('Meeting booked', {
        description: `Your meeting with ${company} is confirmed`,
      });
    },
    
    contactAdded: (name: string) => {
      toast.success('Contact added', {
        description: `${name} added to your network`,
      });
    },
  },
};

/**
 * Example usage:
 * 
 * import { notify } from '@/app/utils/notifications';
 * 
 * // Simple success
 * notify.success('Action completed!');
 * 
 * // With description
 * notify.error('Failed to save', 'Please try again later');
 * 
 * // Loading state
 * const loadingToast = notify.loading('Generating report...');
 * // Later...
 * notify.dismiss(loadingToast);
 * notify.success('Report ready!');
 * 
 * // Custom app notifications
 * notify.custom.sessionSaved();
 * notify.custom.speakerFollowed('Dr. Aris Thorne');
 */
